'use client';

import type { ElementType } from 'react';
import { Card, CardBody, CardFooter } from '@heroui/react';
import Link from 'next/link';
import Image from 'next/image';
import {
  Calendar,
  Gauge,
  Settings,
  Fuel,
  Car,
  MapPin,
  Compass,
  type LucideIcon,
} from 'lucide-react';
import { Vehicle } from '../../utils/types';
import { readableTextOn } from '@/utils/colors';
import { dealershipLabel, etiquetaCatalogo, showVehicleLocation } from '@/utils/functions';
import { useCurrency } from '@/hooks/useCurrency';
import useClientStore from '@/store/useClientStore';
import { useTranslation } from '@/i18n/hooks/useTranslation';
import { isUsa } from '@/lib/country';
import { precioModoDelCliente } from '@/lib/precio-web';
import {
  buildVersionTitle,
  resolveSpecSlots,
  specValue as specValueDeLaTabla,
  type CardSpecSlotProps,
  type CardTitleField,
  type SpecContext,
  type SpecKey,
  type VehicleForCard,
} from '@/lib/vehicleCardFields';

// =============================
// Íconos de la fila de datos
// =============================
// Los cuatro primeros y MapPin son los que ya estaban, con el mismo dato cada
// uno; Compass es el único nuevo, para la tracción.
const SPEC_ICONS: Record<SpecKey, LucideIcon> = {
  mileage: Gauge,
  year: Calendar,
  transmission: Settings,
  fuel: Fuel,
  traction: Compass,
  dealership: MapPin,
};

interface VehicleGridCardProps extends CardSpecSlotProps {
  vehicle: Vehicle;
  cardTitleField?: CardTitleField;
  newBadgeText?: string;
  showBadgeCondition?: boolean;
  showBadgePromo?: boolean;
  showBadgeNew?: boolean;
  showBadgeCustom?: boolean;
  showRibbonSold?: boolean;
  showRibbonReserved?: boolean;
  showBadgeDiscount?: boolean;
  showDealership?: boolean;
  /** Las primeras tarjetas del listado son lo primero que se ve. Sin esto, la
   *  foto que mide el LCP arranca en carga diferida, que es el default de
   *  `next/image`. Solo para las que entran en pantalla sin desplazarse. */
  priorizarImagen?: boolean;
  // Builder color overrides
  cardBgColor?: string;
  cardBorderColor?: string;
  cardTitleColor?: string;
  cardSubtitleColor?: string;
  cardSpecsColor?: string;
  cardPriceColor?: string;
}

/* =============================
 * Tag minimalista y profesional
 * ============================= */
const Tag = ({
  text,
  primary = false,
  bgColor,
}: {
  text: string;
  primary?: boolean;
  bgColor?: string;
}) => {
  if (bgColor) {
    return (
      <span
        className="px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide whitespace-nowrap"
        style={{ backgroundColor: bgColor, color: readableTextOn(bgColor) }}
      >
        {text}
      </span>
    );
  }
  return (
    <span
      className={`
        px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide whitespace-nowrap
        ${primary
          ? 'bg-neutral-900 text-white dark:bg-white dark:text-neutral-900'
          : 'bg-white/90 text-neutral-700 dark:bg-neutral-800/90 dark:text-neutral-200'}
      `}
    >
      {text}
    </span>
  );
};

const VehicleGridCard = ({
  vehicle,
  cardTitleField = 'model',
  newBadgeText = 'Nuevo',
  showBadgeCondition = true,
  showBadgePromo = true,
  showBadgeNew = true,
  showBadgeCustom = true,
  showRibbonSold = true,
  showRibbonReserved = true,
  showBadgeDiscount = true,
  showDealership = true,
  priorizarImagen = false,
  cardBgColor,
  cardBorderColor,
  cardTitleColor,
  cardSubtitleColor,
  cardSpecsColor,
  cardPriceColor,
  cardSpec1,
  cardSpec2,
  cardSpec3,
  cardSpec4,
  cardSpec5,
}: VehicleGridCardProps) => {
  const { formatPrice } = useCurrency();
  // Flag de sitio (Configuración → Sucursales): apagarlo oculta la ubicación del
  // auto en toda la web. Se combina con el prop showDealership (default true).
  const client = useClientStore((s) => s.client);
  const country = client?.country;
  // Ticket #171: cómo se muestran los dos precios (ver `lib/precio-web`).
  const precioModo = precioModoDelCliente(client);
  // Textos fijos de la tarjeta (catalog.*), por el i18n del sitio: un sitio de
  // EE.UU. los ve en inglés (instancia `i18nEn`). Si la clave no resuelve, el
  // español de siempre.
  const { t } = useTranslation();
  const tx = (key: string, fallback: string) => {
    const val = t?.(key) ?? '';
    return !val || val === key ? fallback : val;
  };
  // `specValue` con el país del tenant ya puesto: en Brasil las etiquetas salen
  // en portugués y en EE.UU. en inglés; en cualquier otro país es la misma
  // función de siempre.
  const specValue = (v: VehicleForCard, clave: SpecKey, ctx: SpecContext = {}) =>
    specValueDeLaTabla(v, clave, { ...ctx, country });
  // Respaldo del título cuando el auto no trae marca/modelo: EE.UU. en inglés;
  // el resto, 'Marca'/'Modelo' como siempre.
  const marcaRespaldo = isUsa(country) ? 'Make' : 'Marca';
  const modeloRespaldo = isUsa(country) ? 'Model' : 'Modelo';

  // Estados
  const status = (vehicle.status?.name || '').trim().toLowerCase();
  const isSold = status === 'vendido';
  const isReserved = status === 'reservado';
  const isUnavailable = isSold || isReserved;

  // "Reciente"
  const isNew = () => {
    if (!vehicle.created_at) return false;
    const fiveDaysAgo = new Date();
    fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5);
    return new Date(vehicle.created_at) > fiveDaysAgo;
  };

  // Badge promocional
  const promoBadgeText = (() => {
    if (!showBadgePromo) return undefined;
    if (vehicle.label) return vehicle.label;
    if (!isUnavailable && isNew()) {
      const opts = [
        tx('catalog.card.new', 'Nuevo'),
        tx('catalog.card.recentArrival', 'Recién llegado'),
        tx('catalog.card.opportunity', 'Oportunidad'),
        tx('catalog.card.featured', 'Destacado'),
      ];
      const hash = vehicle.id
        ? vehicle.id.toString().split('').reduce((a, b) => a + b.charCodeAt(0), 0)
        : Math.floor(Math.random() * 1000);
      return newBadgeText === 'Nuevo' ? opts[hash % opts.length] : newBadgeText;
    }
    return undefined;
  })();

  const formattedPrice = formatPrice(vehicle.price);
  const discountedPrice =
    vehicle.discount_percentage && vehicle.discount_percentage > 0
      ? vehicle.price * (1 - vehicle.discount_percentage / 100)
      : null;

  // Mostrar la ubicación es una decisión del TENANT, no de cada auto: o la
  // llevan todas las tarjetas o ninguna. Por eso se separa "corresponde el
  // renglón" de "este auto tiene el dato": un auto sin sucursal deja el lugar
  // vacío en vez de encogerse, y así dos tarjetas miden lo mismo aunque estén
  // en filas distintas (en una sola columna, estirar no alcanza).
  const mostrarUbicacion = showDealership && showVehicleLocation(client);
  const sucursalText = mostrarUbicacion ? dealershipLabel(vehicle.dealership, country) : '';

  // `transmission_type` es un nombre viejo de la columna que todavía llega en
  // algunos payloads; la tarjeta lo aceptaba y se mantiene el alias.
  const vehiculoParaDatos = {
    ...vehicle,
    transmission:
      (vehicle as { transmission?: string | null; transmission_type?: string | null })
        .transmission ??
      (vehicle as { transmission_type?: string | null }).transmission_type ??
      null,
  };

  // ¿El dueño configuró las ranuras desde el builder? Sólo puede ser true si la
  // sección se las mandó, y sólo TraditionalVehicleGrid las manda: `/vehicles` y
  // el carrusel del home no pasan ninguna, igual que toda página guardada antes
  // de que estas props existieran.
  const { configured, keys: specKeys } = resolveSpecSlots({
    cardSpec1,
    cardSpec2,
    cardSpec3,
    cardSpec4,
    cardSpec5,
  });

  const specs = configured
    ? // Configurado: el orden lo pone el dueño y un dato que el auto no tiene no
      // ocupa lugar (un '—' es ruido cuando el dato lo elegiste vos).
      specKeys
        .map((clave) => ({
          clave,
          texto: specValue(vehiculoParaDatos, clave, { dealershipLabel: sucursalText }),
        }))
        .filter((d) => d.texto !== null)
        .map(({ clave, texto }) => {
          const Icono = SPEC_ICONS[clave];
          return { icon: <Icono size={16} className="opacity-80" />, text: texto };
        })
    : // Heredado: la misma lista de siempre, en el mismo orden, con el mismo '—'
      // y la sucursal al final sólo si el auto tiene una. Es el camino que corren
      // TODAS las páginas publicadas hasta hoy y las otras dos superficies que
      // usan esta tarjeta, así que no se toca. Las etiquetas salen de `specValue`
      // —la misma función que usa el otro camino— para no tener dos tablas de
      // combustibles que se separen con el tiempo.
      [
        { icon: <Gauge size={16} className="opacity-80" />, text: specValue(vehiculoParaDatos, 'mileage') ?? '—' },
        { icon: <Calendar size={16} className="opacity-80" />, text: specValue(vehiculoParaDatos, 'year') ?? '—' },
        { icon: <Settings size={16} className="opacity-80" />, text: specValue(vehiculoParaDatos, 'transmission') ?? '—' },
        { icon: <Fuel size={16} className="opacity-80" />, text: specValue(vehiculoParaDatos, 'fuel') ?? '—' },
        ...(mostrarUbicacion
          ? [{ icon: <MapPin size={16} className="opacity-80" />, text: sucursalText }]
          : []),
      ];

  // Brasil: la condición de la base ("semi-nuevo") se muestra en portugués
  // ("Seminovo"); el resto, la capitalización de siempre.
  const conditionText = vehicle.condition?.name
    ? etiquetaCatalogo(
        'condition',
        vehicle.condition.name,
        country,
        vehicle.condition.name.charAt(0).toUpperCase() + vehicle.condition.name.slice(1).toLowerCase()
      )
    : undefined;

  const backendArrays = [
    (vehicle as any).badges,
    (vehicle as any).tags,
    (vehicle as any).labels,
  ].filter(Array.isArray) as string[][];

  const setLower = new Set<string>();
  const addUnique = (arr: string[], val?: string) => {
    if (!val) return arr;
    const v = val.trim();
    if (!setLower.has(v.toLowerCase())) {
      setLower.add(v.toLowerCase());
      arr.push(v);
    }
    return arr;
  };

  let leftChips: string[] = [];
  if (showBadgeCondition) leftChips = addUnique(leftChips, conditionText);
  if (showBadgeNew && isNew()) leftChips = addUnique(leftChips, tx('catalog.card.recentArrival', 'Recién llegado'));
  if (showBadgeCustom) backendArrays.forEach((a) => a.forEach((v) => addUnique(leftChips, v)));
  if (promoBadgeText) leftChips = leftChips.filter((c) => c.toLowerCase() !== promoBadgeText.toLowerCase());
  
  // Link real (crawleable, middle-click, teclado) para disponibles; los
  // Vendido/Reservado mantienen el div no navegable de siempre.
  // prefetch={false} OBLIGATORIO: la ruta es dinámica — cada prefetch sería
  // middleware + query a Supabase por tarjeta visible, en ~200 tenants.
  const Wrapper: ElementType = isUnavailable ? 'div' : Link;
  const wrapperProps = isUnavailable
    ? {}
    : { href: `/vehicles/${vehicle.id}`, prefetch: false };

  return (
    <Wrapper
      {...wrapperProps}
      // Sin `h-full`: un `height:100%` no es `auto`, así que apaga el
      // `align-items:stretch` del contenedor y, contra un alto indefinido, cae
      // a alto de contenido. Sin él, el wrapper se estira solo con la fila y el
      // Card lo llena con `flex-1`.
      className={`group flex flex-col outline-none ${!isUnavailable ? 'cursor-pointer' : 'cursor-default'}`}
    >
      <Card
        className={`flex-1 flex flex-col overflow-hidden rounded-2xl
        ${cardBgColor ? '' : 'bg-white dark:bg-[#0B0B0F]'}
        ${cardBorderColor ? '' : 'border border-black/10 dark:border-transparent'}
        shadow-sm transition-all duration-300
        ${isUnavailable ? 'opacity-90' : 'group-hover:shadow-xl group-hover:-translate-y-2'}`}
        style={{
          ...(cardBgColor ? { backgroundColor: cardBgColor } : {}),
          ...(cardBorderColor ? { borderWidth: 1, borderStyle: 'solid', borderColor: cardBorderColor } : {}),
        }}
      >
        <div className="relative w-full aspect-[16/9] overflow-hidden bg-black">
          {vehicle.main_image ? (
            <Image
              src={vehicle.main_image}
              alt={`${vehicle.brand?.name ?? ''} ${vehicle.model?.name ?? ''} ${vehicle.year ?? ''}`.trim() || tx('catalog.common.vehicle', 'Vehículo')}
              fill
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 25vw"
              className="object-cover"
              priority={priorizarImagen}
            />
          ) : (
            <div className="absolute inset-0 grid place-items-center bg-neutral-200 dark:bg-neutral-900">
              <Car size={48} className="text-neutral-400" />
            </div>
          )}

          {/* Ribbons */}
          {showRibbonSold && isSold && (
            <div className="absolute top-0 right-0 h-[200px] w-[200px] overflow-hidden z-20">
              <div className="absolute top-[30px] right-[-50px] bg-rose-600 text-white font-bold py-2 w-[250px] text-center rotate-45">
                {/* EE.UU.: en inglés. El resto, la palabra de siempre. */}
                {isUsa(country) ? tx('vehicles.status.sold', 'Vendido').toUpperCase() : 'VENDIDO'}
              </div>
            </div>
          )}
          {showRibbonReserved && isReserved && (
            <div className="absolute top-0 right-0 h-[200px] w-[200px] overflow-hidden z-20">
              <div className="absolute top-[30px] right-[-50px] bg-amber-400 text-black font-bold py-2 w-[250px] text-center rotate-45">
                {isUsa(country) ? tx('vehicles.status.reserved', 'Reservado').toUpperCase() : 'RESERVADO'}
              </div>
            </div>
          )}

          {/* Tags minimalistas en esquina superior izquierda */}
          {(promoBadgeText || leftChips.length > 0) && (
            <div className="absolute top-2.5 left-2.5 right-2.5 z-20 flex flex-wrap items-start gap-1.5">
              {promoBadgeText && (
                <Tag
                  text={promoBadgeText}
                  primary
                  bgColor={vehicle.label ? vehicle.label_color : undefined}
                />
              )}
              {leftChips.map((txt, i) => (
                <Tag key={i} text={txt} />
              ))}
            </div>
          )}
        </div>

        {/* CONTENIDO */}
        <CardBody className="flex-1 px-5 pt-4 pb-2">
          <h3
            className={`text-[20px] sm:text-[22px] font-semibold tracking-tight ${cardTitleColor ? '' : 'text-neutral-900 dark:text-white'}`}
            style={cardTitleColor ? { color: cardTitleColor } : undefined}
          >
            {cardTitleField === 'brand_model_version'
              ? buildVersionTitle(vehiculoParaDatos, country)
              : cardTitleField === 'brand_model'
                ? `${vehicle.brand?.name ?? marcaRespaldo} ${vehicle.model?.name ?? ''}`.trim()
                : cardTitleField === 'brand'
                  ? (vehicle.brand?.name ?? marcaRespaldo)
                  : (vehicle.model?.name ?? modeloRespaldo)}
          </h3>
          <p
            className={`mt-0.5 text-sm ${cardSubtitleColor ? '' : 'text-neutral-600 dark:text-neutral-400'}`}
            style={cardSubtitleColor ? { color: cardSubtitleColor } : undefined}
          >
            {cardTitleField === 'brand_model' || cardTitleField === 'brand_model_version'
              ? (vehicle.year ?? '')
              : `${cardTitleField === 'brand' ? vehicle.model?.name : vehicle.brand?.name} ${vehicle.year ?? ''}`}
          </p>

          <div
            className={`mt-3 grid grid-cols-2 gap-x-5 gap-y-2 text-[13px] ${cardSpecsColor ? '' : 'text-neutral-800 dark:text-neutral-300'}`}
            style={cardSpecsColor ? { color: cardSpecsColor } : undefined}
          >
            {specs.map((s, i) => (
              // Sin dato, la celda queda vacía pero ocupa lo mismo: el ícono se
              // esconde con `invisible` (sigue midiendo) y el texto va con un
              // espacio duro, que si no el renglón colapsa a cero.
              <div key={i} className="flex items-center gap-1.5 min-w-0" aria-hidden={s.text ? undefined : true}>
                <span className={s.text ? undefined : 'invisible'}>{s.icon}</span>
                <span className="truncate">{s.text || ' '}</span>
              </div>
            ))}
          </div>

          {Array.isArray(vehicle.features) && vehicle.features.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {vehicle.features.slice(0, 3).map((f, i) => (
                <span
                  key={i}
                  className="px-2 py-0.5 rounded-full text-[11px] leading-5 bg-neutral-100 text-neutral-700 dark:bg-white/5 dark:text-neutral-200"
                >
                  {f}
                </span>
              ))}
            </div>
          )}
        </CardBody>

        {/* FOOTER */}
        <CardFooter className="px-5 pb-5 pt-3">
          <div className="w-full flex items-end justify-between">
            <div className="flex flex-col">
              {showBadgeDiscount && discountedPrice && precioModo === 'contado_financiamiento' ? (
                // Ticket #171: dos formas de pago, cada una con su rótulo y sin tachar.
                <span className="flex flex-col" data-precio-modo="contado_financiamiento">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                    {tx('catalog.card.financingPrice', 'Precio financiamiento')}
                  </span>
                  <span
                    className={`text-2xl font-extrabold tracking-tight ${cardPriceColor ? '' : 'text-primary'}`}
                    style={cardPriceColor ? { color: cardPriceColor } : undefined}
                  >
                    {formatPrice(discountedPrice)}
                  </span>
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400 mt-1">
                    {tx('catalog.card.cashPrice', 'Precio contado')}
                  </span>
                  <span className="text-base font-semibold text-neutral-700 dark:text-neutral-300">
                    {formattedPrice}
                  </span>
                </span>
              ) : showBadgeDiscount && discountedPrice ? (
                <>
                  <span className="text-sm line-through text-neutral-500 dark:text-neutral-400">
                    {formattedPrice}
                  </span>
                  <span
                    className={`text-2xl font-extrabold tracking-tight ${cardPriceColor ? '' : 'text-primary'}`}
                    style={cardPriceColor ? { color: cardPriceColor } : undefined}
                  >
                    {formatPrice(discountedPrice)}
                  </span>
                </>
              ) : (
                <span
                  className={`text-2xl font-extrabold tracking-tight ${cardPriceColor ? '' : 'text-neutral-900 dark:text-white'}`}
                  style={cardPriceColor ? { color: cardPriceColor } : undefined}
                >
                  {formattedPrice}
                </span>
              )}
            </div>

          </div>
        </CardFooter>
      </Card>
    </Wrapper>
  );
};

export default VehicleGridCard;
